import React, { useState, useEffect, useRef, useMemo } from 'react';
import RightModal from "../components/templates/RightModal";
import { getJobsList, selectJobsData, selectJobsLoading } from "../redux/jobs/jobsSlice";
import DynamicIcon from "../components/DynamicIcon";
import ViewJob from "../components/jobsPage/ViewJob";
import LoadingSection from "../components/LoadingSection";
import { selectAccountData } from "../redux/main/accountSlice";
import { resetRefreshNow, selectRightModalId, selectRightModalIsOpen, selectRightModalIsRefreshNow, selectRightModalmode, setModal } from "../redux/rightModalSlice";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from 'react-router-dom';
import DynamicTitle from "../components/DynamicTitle";
import CreateJob from './CreateJob';
import { useColorPalette } from '../hooks/useColorPalette';
import DataTable from '../components/DataTable';
import moment from "moment";
import Select2 from '../components/Select2';
import { toast } from 'react-toastify';

export default function JobsListToBill() {
    const dispatch = useDispatch();
    const palette = useColorPalette();
    const navigate = useNavigate();
    const jobsList = useSelector(selectJobsData);
    const jobsLoading = useSelector(selectJobsLoading);
    const accountData = useSelector(selectAccountData);
    const token = localStorage.getItem('installamelo-auth-token');
    const [searchTerm, setSearchTerm] = useState('');
    const [jobTypeFilter, setJobTypeFilter] = useState('');
    
    // Stati per la modale di cambio stato
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [statusToUpdate, setStatusToUpdate] = useState(null);
    const [jobToUpdateStatus, setJobToUpdateStatus] = useState(null);
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

    // Trova l'ID dello stato "dafatturare" dai settings dell'account
    const daFatturareStatusId = useMemo(() => {
        const daFatturareStatus = accountData?.settings?.jobStatus?.find(
            status => status.code === 'dafatturare'
        );
        return daFatturareStatus?._id || '';
    }, [accountData?.settings?.jobStatus]);

    const searchTimeout = useRef(null);
    
    // RightModal gestita tramite Redux
    const rightModalIsOpen = useSelector(selectRightModalIsOpen);
    const rightModalMode = useSelector(selectRightModalmode);
    const rightModalJobId = useSelector(selectRightModalId);

    const jobStatusOptions = useMemo(() => {
        return accountData?.settings?.jobStatus?.map(status => ({ 
            id: status._id, 
            text: status.label 
        })) || [];
    }, [accountData?.settings?.jobStatus]);
    
    // Funzione che verrà passata al componente DataTable per gestire la paginazione
    const handlePageChange = (pageIndex, pageSize) => {
        // Calcoliamo l'offset start in base all'indice di pagina
        const start = pageIndex * pageSize;
        
        // Eseguo la chiamata per ottenere i dati con i nuovi parametri di paginazione
        dispatch(getJobsList({
            accountId: accountData?._id,
            token,
            search: searchTerm.length >= 2 ? searchTerm : '',
            start,
            length: pageSize,
            jobStatusId: daFatturareStatusId,
            jobTypeId: jobTypeFilter
        }));
        dispatch(resetRefreshNow());
    };

    const handleSearch = (value) => {
        setSearchTerm(value);

        // Debounce della ricerca
        if (searchTimeout.current) {
            clearTimeout(searchTimeout.current);
        }

        searchTimeout.current = setTimeout(() => {
            dispatch(getJobsList({
                accountId: accountData?._id,
                token,
                search: value.length >= 2 ? value : '',
                start: 0,
                length: 25,
                jobStatusId: daFatturareStatusId,
                jobTypeId: jobTypeFilter
            }));
            dispatch(resetRefreshNow());
        }, 300); // Esegui la ricerca dopo 300ms di inattività
    };

    // Chiamata API ogni volta che cambiano i filtri o la ricerca
    useEffect(() => {
        if (accountData?._id && daFatturareStatusId) {
            dispatch(getJobsList({
                accountId: accountData._id,
                token,
                search: searchTerm.length >= 2 ? searchTerm : '',
                start: 0,
                length: 25,
                jobStatusId: daFatturareStatusId,
                jobTypeId: jobTypeFilter
            }));
            dispatch(resetRefreshNow());
        }
    }, [accountData?._id, searchTerm, daFatturareStatusId, jobTypeFilter, dispatch, token]);

    const jobTypeOptions = accountData?.settings?.jobTypes || [];
    
    // Funzione per chiudere la modale di cambio stato
    const handleCloseStatusModal = () => {
        setShowStatusModal(false);
        setJobToUpdateStatus(null);
        setStatusToUpdate(null);
    };
    
    // Funzione per aggiornare lo stato della commessa
    const handleUpdateStatus = async () => {
        if (!jobToUpdateStatus || !statusToUpdate) return;
        
        try {
            setIsUpdatingStatus(true);
            
            // Trova i dettagli del nuovo stato dalla lista degli stati disponibili
            const newStatus = accountData.settings.jobStatus.find(s => s._id === statusToUpdate);
            if (!newStatus) {
                throw new Error('Stato selezionato non valido');
            }
            
            // Prepara l'oggetto commessa aggiornato con il nuovo stato
            const updatedJob = {
                ...jobToUpdateStatus,
                statusId: newStatus._id,
                statusLabel: newStatus.label
            };
            
            // Chiamata API per aggiornare lo stato
            const payload = {
                job: updatedJob
            };
            
            const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || ''}/jobs`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'auth-token': token
                },
                body: JSON.stringify(payload)
            });
            
            if (!response.ok) {
                throw new Error(`Errore nella risposta: ${response.status}`);
            }
            
            // Ricarica la lista delle commesse per aggiornare i dati
            dispatch(getJobsList({
                accountId: accountData._id,
                token,
                search: searchTerm.length >= 2 ? searchTerm : '',
                start: 0,
                length: 25,
                jobStatusId: daFatturareStatusId,
                jobTypeId: jobTypeFilter
            }));
            
            // Chiudi la modale e mostra un messaggio di successo
            handleCloseStatusModal();
            toast.success(`Stato aggiornato a: ${newStatus.label}`);
        } catch (error) {
            console.error('Errore durante l\'aggiornamento dello stato:', error);
            toast.error(`Errore durante l'aggiornamento: ${error.message}`);
        } finally {
            setIsUpdatingStatus(false);
        }
    };

    if (!palette) {
        return <LoadingSection isFixed />;
    }

    return (
        <div className="page">
            {jobsLoading && <LoadingSection isFixed />}
            <DynamicTitle title="Commesse da Fatturare" />
            
            <div className="flex justify-between items-center mb-4">
                <h1 className="text-2xl font-bold">Commesse da Fatturare</h1>
                <button 
                    onClick={() => navigate('/createJob')}
                    style={{ 
                        backgroundColor: palette.primary, 
                        color: palette.textOnPrimary, 
                        padding: '0.5rem 1rem', 
                        borderRadius: '0.375rem',
                        border: 'none',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2a2a2a'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = palette.primary}
                >
                    <DynamicIcon iconClass="fas fa-plus" />
                    Nuova Commessa
                </button>
            </div>
            <div className="box">
                <div className="w-full mb-4">
                    <div className="flex flex-col md:flex-row gap-4 md:items-center">
                        {/* Campo di ricerca */}
                        <div className="relative w-full">
                            <input 
                                type="text" 
                                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 pr-10" 
                                placeholder="Cerca commessa..." 
                                value={searchTerm}
                                onChange={(e) => handleSearch(e.target.value)}
                            />
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                <DynamicIcon iconClass="fa-solid fa-search" />
                            </div>
                        </div>
                        
                        {/* Stato fisso - non modificabile */}
                        <div className="hidden">
                            {/* Nascosto ma manteniamo il componente per mantenere la struttura del codice */}
                            <Select2 
                                id="jobStatusFilter"
                                placeholder="Filtra per stato..."
                                allowClear={false}
                                options={jobStatusOptions}
                                value={jobStatusOptions.find(opt => opt.id === daFatturareStatusId) || null}
                                onChange={() => {}} // Funzione vuota perché non deve essere modificabile
                                minimumInputLength={0}
                                disabled={true}
                            />
                        </div>
                    </div>
                </div>

                {/* Definizione colonne per DataTable */}
                <DataTable 
                    columns={[
                        { id: 'code', title: 'CODICE / DATA', width: '12%' },
                        { id: 'customer', title: 'CLIENTE', width: '22%' },
                        { id: 'jobType', title: 'TIPO', width: '14%' },
                        { id: 'system', title: 'IMPIANTO', width: '25%' },
                        { id: 'status', title: 'STATO', width: '16%' },
                        { id: 'actions', title: 'AZIONI', width: '5%' }
                    ]}
                    data={jobsList?.data || []}
                    loading={jobsLoading}
                    totalRecords={jobsList?.recordsFiltered || 0}
                    pageSize={25}
                    onPageChange={handlePageChange}
                    onSearch={handleSearch}
                    emptyMessage="Nessuna commessa da fatturare trovata"
                    renderCustomRow={(job, index, isEven, tableStyles) => {
                        // Formatta la data di creazione
                        const formattedDate = job.creationDate 
                            ? moment(job.creationDate).format("DD/MM/YYYY")
                            : "--/--/----";
                            
                        // Funzione per determinare lo stile dello stato utilizzando i colori da accountData.settings.jobStatus
                        const getStatusStyle = () => {
                            // Stile di base
                            const baseStyle = {
                                padding: '0.25rem 0.5rem',  // px-2 py-1 equivalent
                                borderRadius: '9999px',    // rounded-full equivalent
                                fontSize: '0.875rem',      // text-sm equivalent
                                display: 'inline-block',
                                textAlign: 'center'
                            };
                            
                            // Cerca lo stato corrente nelle impostazioni dell'account
                            const currentStatus = accountData?.settings?.jobStatus?.find(
                                status => status._id === job.statusId
                            );
                            
                            // Se troviamo lo stato e ha un colore, lo applichiamo
                            if (currentStatus && currentStatus.color) {
                                return {
                                    ...baseStyle,
                                    backgroundColor: `${currentStatus.color}20`,  // Colore con opacità del 20%
                                    color: currentStatus.color,
                                    border: `1px solid ${currentStatus.color}`
                                };
                            }
                            
                            // Stile di fallback se non troviamo lo stato
                            return {
                                ...baseStyle,
                                backgroundColor: '#f3f4f6',  // bg-gray-100
                                color: '#374151'             // text-gray-700
                            };
                        };
                        
                        // Funzioni per gestire le azioni
                        const handleViewClick = (e) => {
                            e.stopPropagation();
                            dispatch(setModal({
                                mode: 'view',
                                id: job._id
                            }));
                        };
                        
                        const handleEditClick = (e) => {
                            e.stopPropagation();
                            navigate(`/job/edit/${job._id}`);
                        };
                        
                        // Funzione per verificare se la commessa può essere pianificata
                        const canScheduleJob = () => {
                            if (!accountData?.settings?.jobStatus || !job.statusId) return true;
                            
                            // Trova gli stati "fatturata" e "archiviata"
                            const disabledStatusCodes = ['fatturata', 'archiviata'];
                            const disabledStatusIds = accountData.settings.jobStatus
                                .filter(status => disabledStatusCodes.includes(status.code))
                                .map(status => status._id);
                            
                            // Verifica se lo stato corrente della commessa è tra quelli disabilitati
                            return !disabledStatusIds.includes(job.statusId);
                        };
                        
                        const handleScheduleClick = (e) => {
                            e.stopPropagation();
                            if (canScheduleJob()) {
                                navigate(`/jobs/scheduling?jobId=${job._id}`);
                            }
                        };
                        
                        const handleRowClick = () => {
                            dispatch(setModal({
                                mode: 'view',
                                id: job._id
                            }));
                        };
                        
                        const handleStatusChangeClick = (e) => {
                            e.stopPropagation();
                            
                            if (!job) {
                                console.error('Job non definita!');
                                return;
                            }
                            
                            if (!accountData?.settings?.jobStatus) {
                                console.error('Settings degli stati commessa non disponibili!');
                                return;
                            }
                            
                            setJobToUpdateStatus(job);
                            setStatusToUpdate(job.statusId || accountData.settings.jobStatus[0]?._id);
                            setShowStatusModal(true);
                        };
                        
                        return (
                            <div 
                                className={`data-table-row ${isEven ? 'bg-gray-50' : 'bg-white'} hover:bg-gray-100 cursor-pointer`} 
                                key={job._id}
                                onClick={handleRowClick}
                            >
                                <div className="data-table-cell" style={{ width: '12%' }} data-label="Codice">
                                    <div className="flex flex-col">
                                        <span className="font-medium">{job.code || "N/D"}</span>
                                        <span className="text-xs text-gray-500">{formattedDate}</span>
                                    </div>
                                </div>
                                
                                <div className="data-table-cell" style={{ width: '22%' }} data-label="Cliente">
                                    <span>{job.customerLabel || "Cliente non specificato"}</span>
                                </div>
                                
                                <div className="data-table-cell" style={{ width: '14%' }} data-label="Tipo">
                                    <span>{job.jobTypeLabel || "Tipo non specificato"}</span>
                                </div>
                                
                                <div className="data-table-cell" style={{ width: '25%' }} data-label="Impianto">
                                    <div>
                                        <span>{job.systemLabel || "Impianto non specificato"}</span>
                                        {job.systemAddressLabel && (
                                            <div className="text-xs text-gray-600">
                                                {job.systemAddressLabel}
                                            </div>
                                        )}
                                        {job.geoData && (
                                            <div className="text-xs text-gray-600 mt-1">
                                                {job.geoData.distanceKm !== undefined ? `${parseInt(job.geoData.distanceKm)} km` : "--"} 
                                                {job.geoData.distanceTime !== undefined ? ` • ${parseInt(job.geoData.distanceTime)} min` : ""}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                
                                <div className="data-table-cell" style={{ width: '16%' }} data-label="Stato">
                                    <span style={getStatusStyle()}>
                                        {job.statusLabel || "Non definito"}
                                    </span>
                                </div>
                                
                                <div className="data-table-cell" style={{ width: '5%' }} data-label="Azioni">
                                    <div className="flex justify-center space-x-2">
                                        <button 
                                            className="p-1 hover:bg-blue-100 rounded-full"
                                            onClick={handleViewClick}
                                            title="Visualizza"
                                        >
                                            <DynamicIcon iconClass="fa-solid fa-eye" />
                                        </button>
                                        <button 
                                            className="p-1 hover:bg-blue-100 rounded-full"
                                            onClick={handleEditClick}
                                            title="Modifica"
                                        >
                                            <DynamicIcon iconClass="fa-solid fa-edit" />
                                        </button>
                                        <button 
                                            className={`p-1 rounded-full ${canScheduleJob() ? 'hover:bg-green-100' : 'cursor-not-allowed opacity-50'}`}
                                            onClick={handleScheduleClick}
                                            title={canScheduleJob() ? "Pianifica" : "Non pianificabile (commessa fatturata o archiviata)"}
                                            disabled={!canScheduleJob()}
                                        >
                                            <DynamicIcon iconClass={`fa-solid fa-calendar ${canScheduleJob() ? 'text-green-600' : 'text-gray-400'}`} />
                                        </button>
                                        <button 
                                            className="p-1 hover:bg-orange-100 rounded-full"
                                            onClick={handleStatusChangeClick}
                                            title="Cambia Stato"
                                        >
                                            <DynamicIcon iconClass="fa-solid fa-arrows-rotate text-orange-600" />
                                        </button>
                                        <button 
                                            className="p-1 hover:bg-red-100 rounded-full"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                // Qui dovrebbe essere gestita l'eliminazione della commessa
                                                // con una finestra di conferma
                                            }}
                                            title="Elimina"
                                        >
                                            <DynamicIcon iconClass="fa-solid fa-trash text-red-600" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    }}
                />
            </div>

            {/* RIGHT MODAL solo per la visualizzazione del dettaglio commessa */}
            {
                rightModalIsOpen && (
                    <RightModal>
                        {
                            rightModalMode === 'view' && rightModalJobId && (
                                <ViewJob jobId={rightModalJobId} />
                            )
                        }
                    </RightModal>
                )
            }
            
            {/* Modale per il cambio di stato */}
            {showStatusModal && (
                <div 
                    style={{
                        display: 'block', 
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        width: '100vw',
                        height: '100vh',
                        zIndex: 9999,
                        overflow: 'auto',
                        paddingLeft: 0,
                        paddingRight: 0
                    }} 
                    tabIndex="-1"
                >
                    <div style={{
                        position: 'relative',
                        width: '500px',
                        maxWidth: '90%',
                        margin: '10vh auto',
                        backgroundColor: '#fff',
                        borderRadius: '5px',
                        boxShadow: '0 3px 9px rgba(0,0,0,.5)',
                        animation: 'fadeIn 0.3s'
                    }}>
                        <div style={{ backgroundColor: '#fff', border: 'none', borderRadius: '5px', overflow: 'hidden' }}>
                            <div style={{ padding: '15px', borderBottom: '1px solid #e5e5e5', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <h5 style={{ margin: 0, fontWeight: 'bold' }}>Cambia Stato Commessa</h5>
                                <button 
                                    type="button" 
                                    onClick={handleCloseStatusModal}
                                    style={{
                                        border: 'none',
                                        background: 'transparent',
                                        fontSize: '20px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    &times;
                                </button>
                            </div>
                            <div style={{ padding: '15px' }}>
                                {jobToUpdateStatus && (
                                    <div>
                                        <p><strong>Cliente:</strong> {jobToUpdateStatus.customerLabel || 'N/D'}</p>
                                        <p><strong>Codice:</strong> {jobToUpdateStatus.code || 'N/D'}</p>
                                        <p><strong>Tipo:</strong> {jobToUpdateStatus.jobTypeLabel || 'N/D'}</p>
                                        
                                        <div style={{ marginTop: '20px', marginBottom: '15px' }}>
                                            <label htmlFor="status-select" style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>Seleziona nuovo stato:</label>
                                            <select
                                                id="status-select"
                                                value={statusToUpdate}
                                                onChange={(e) => setStatusToUpdate(e.target.value)}
                                                disabled={isUpdatingStatus}
                                                style={{
                                                    width: '100%',
                                                    padding: '8px 12px',
                                                    border: '1px solid #ced4da',
                                                    borderRadius: '4px',
                                                    appearance: 'auto'
                                                }}
                                            >
                                                {accountData?.settings?.jobStatus?.map((status) => (
                                                    <option key={status._id} value={status._id}>
                                                        {status.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div style={{ padding: '15px', borderTop: '1px solid #e5e5e5', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                                <button 
                                    type="button" 
                                    onClick={handleCloseStatusModal}
                                    disabled={isUpdatingStatus}
                                    style={{
                                        padding: '8px 16px',
                                        backgroundColor: '#6c757d',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: isUpdatingStatus ? 'not-allowed' : 'pointer',
                                        opacity: isUpdatingStatus ? 0.65 : 1
                                    }}
                                >
                                    Annulla
                                </button>
                                <button 
                                    type="button" 
                                    onClick={handleUpdateStatus}
                                    disabled={isUpdatingStatus}
                                    style={{
                                        padding: '8px 16px',
                                        backgroundColor: palette.primary,
                                        color: palette.textOnPrimary,
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: isUpdatingStatus ? 'not-allowed' : 'pointer',
                                        opacity: isUpdatingStatus ? 0.65 : 1
                                    }}
                                >
                                    {isUpdatingStatus ? 'Aggiornamento...' : 'Aggiorna Stato'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
